import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const Button = styled.button`
  position: fixed !important;
  left: 25px !important;
  bottom: 25px !important;

  width: 65px !important;
  height: 65px !important;

  padding: 0 !important;
  margin: 0 !important;

  border: none !important;
  border-radius: 50% !important;

  background: #e00000 !important;
  color: #ffffff !important;

  font-size: 30px !important;
  line-height: 1 !important;

  cursor: pointer !important;

  z-index: 214748364 !important;

  box-shadow: 0 0 25px red !important;

  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  box-sizing: border-box !important;

  &:hover {
    background: #ff0000 !important;
  }

  &:focus {
    outline: 3px solid #ffffff !important;
    outline-offset: 3px !important;
  }
`;

const Box = styled.div`
  position: fixed !important;

  top: 0 !important;
  left: 0 !important;

  width: 340px !important;
  max-width: 90vw !important;
  height: 100vh !important;

  padding: 25px !important;

  margin: 0 !important;

  box-sizing: border-box !important;

  overflow-x: hidden !important;
  overflow-y: auto !important;

  background: linear-gradient(145deg, #111111, #300000) !important;

  color: #ffffff !important;

  z-index: 2147483647 !important;

  box-shadow: 0 0 40px red !important;

  font-family: Arial, sans-serif !important;
  font-size: 16px !important;

  border-right: 2px solid red !important;

  transform: ${({ open }) =>
    open ? "translateX(0)" : "translateX(-110%)"} !important;

  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) !important;

  will-change: transform;

  direction: rtl !important;

  .header {
    width: 100% !important;

    display: flex !important;
    justify-content: center !important;
    align-items: center !important;

    position: relative !important;

    margin-bottom: 10px !important;
  }

  h3 {
    color: #ffffff !important;

    font-family: Arial, sans-serif !important;

    font-size: 24px !important;

    line-height: 1.2 !important;

    margin: 0 0 25px 0 !important;
    padding: 0 !important;
  }

  .close {
    position: absolute !important;

    right: 0 !important;
    top: 0 !important;

    width: 35px !important;
    height: 35px !important;

    min-width: 35px !important;

    padding: 0 !important;
    margin: 0 !important;

    border: none !important;
    border-radius: 50% !important;

    background: #000000 !important;

    color: #ffffff !important;

    font-size: 20px !important;
    line-height: 1 !important;

    cursor: pointer !important;

    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    box-sizing: border-box !important;
  }

  .close:hover {
    background: #222222 !important;
  }

  .close:focus {
    outline: 3px solid #ffffff !important;
    outline-offset: 2px !important;
  }

  button:not(.close) {
    width: 100% !important;

    min-height: 48px !important;

    margin: 6px 0 !important;
    padding: 13px !important;

    border: none !important;
    border-radius: 12px !important;

    background: #e00000 !important;

    color: #ffffff !important;

    font-family: Arial, sans-serif !important;

    font-size: 16px !important;
    font-weight: 900 !important;

    line-height: 1.3 !important;

    cursor: pointer !important;

    box-sizing: border-box !important;

    text-align: center !important;
  }

  button:not(.close):hover {
    background: #ff0000 !important;
  }

  button:not(.close):focus {
    outline: 3px solid #ffffff !important;
    outline-offset: 2px !important;
  }

  .zoom {
    width: 100% !important;

    text-align: center !important;

    color: #ffffff !important;

    font-family: Arial, sans-serif !important;

    font-weight: 900 !important;

    font-size: 20px !important;

    line-height: 1.3 !important;

    margin: 0 0 15px 0 !important;
    padding: 0 !important;
  }
`;

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    const savedZoom = localStorage.getItem("accessibility-zoom");

    if (savedZoom) {
      const value = Number(savedZoom);

      if (!isNaN(value) && value >= 70 && value <= 200) {
        setZoom(value);
        applyZoom(value);
      }
    }
  }, []);

  const applyZoom = (value) => {
    const root = document.getElementById("root");

    if (root) {
      if (value === 100) {
        root.style.zoom = "";
      } else {
        root.style.zoom = `${value}%`;
      }
    }
  };

  const changeZoom = (amount) => {
    let newValue = zoom + amount;

    if (newValue < 70) {
      newValue = 70;
    }

    if (newValue > 200) {
      newValue = 200;
    }

    setZoom(newValue);

    applyZoom(newValue);

    localStorage.setItem(
      "accessibility-zoom",
      String(newValue)
    );
  };

  const resetZoom = () => {
    setZoom(100);

    applyZoom(100);

    localStorage.removeItem("accessibility-zoom");
  };

  const toggleClass = (className) => {
    document.body.classList.toggle(className);
  };

  const highlightLinks = () => {
    document
      .querySelectorAll("#root a")
      .forEach((link) => {
        link.dataset.accessibilityHighlighted = "true";

        link.style.textDecoration = "underline";
        link.style.textDecorationThickness = "2px";
        link.style.textUnderlineOffset = "3px";
        link.style.fontWeight = "900";
      });
  };

  const resetHighlightedLinks = () => {
    document
      .querySelectorAll(
        '#root a[data-accessibility-highlighted="true"]'
      )
      .forEach((link) => {
        link.style.textDecoration = "";
        link.style.textDecorationThickness = "";
        link.style.textUnderlineOffset = "";
        link.style.fontWeight = "";

        delete link.dataset.accessibilityHighlighted;
      });
  };

  const reset = () => {
    document.body.classList.remove(
      "high-contrast",
      "gray-mode",
      "easy-font",
      "large-space",
      "stop-animation"
    );

    resetHighlightedLinks();

    setZoom(100);

    applyZoom(100);

    localStorage.removeItem("accessibility-zoom");
  };

  return createPortal(
    <>
      <Button
        type="button"
        aria-label="פתיחת תפריט נגישות"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        ♿
      </Button>

      <Box
        open={open}
        role="dialog"
        aria-label="תפריט נגישות"
        aria-modal="false"
      >
        <div className="header">
          <h3>נגישות</h3>

          <button
            type="button"
            className="close"
            aria-label="סגירת תפריט נגישות"
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>

        <div className="zoom">
          🔍 זום {zoom}%
        </div>

        <button
          type="button"
          onClick={() => changeZoom(10)}
        >
          🔍➕ הגדלה
        </button>

        <button
          type="button"
          onClick={() => changeZoom(-10)}
        >
          🔍➖ הקטנה
        </button>

        <button
          type="button"
          onClick={resetZoom}
        >
          ↩ חזרה ל־100%
        </button>

        <button
          type="button"
          onClick={() => toggleClass("high-contrast")}
        >
          ⚫ ניגודיות גבוהה
        </button>

        <button
          type="button"
          onClick={() => toggleClass("gray-mode")}
        >
          ⚪ גווני אפור
        </button>

        <button
          type="button"
          onClick={() => toggleClass("easy-font")}
        >
          📖 גופן קריא
        </button>

        <button
          type="button"
          onClick={() => toggleClass("large-space")}
        >
          ↔ רווחים גדולים
        </button>

        <button
          type="button"
          onClick={() => toggleClass("stop-animation")}
        >
          ⏸ עצירת אנימציות
        </button>

        <button
          type="button"
          onClick={highlightLinks}
        >
          🔗 הדגשת קישורים
        </button>

        <button
          type="button"
          onClick={reset}
        >
          ♻ איפוס
        </button>
      </Box>
    </>,
    document.body
  );
}